import Chevron from "components/Chevron";
import ProfileCircle from 'components/ProfileCircle';
import React, { CSSProperties, useState } from "react";
import { colors } from "utils/constants";

type PositionLeaderboardRow = {
  initials: string | null;
  leaderboard_photo_location: string | null;
  leaderboard_username: string | null;
  points?: number;
};

type LeaderboardRowGroup = {
  position: number;
  positionLeaderboardRows: PositionLeaderboardRow[];
};

interface ILeaderboardTableRowProps {
  rank: number;
  leaderboardRowGroup: LeaderboardRowGroup;
  style?: CSSProperties;
}

export const LeaderboardTableRow: React.FC<ILeaderboardTableRowProps> = ({ rank, leaderboardRowGroup, style }) => {
  let rankShadow: string, crownImageName: string | undefined;

  switch (rank) {
    case 1:
      rankShadow = '2px -2.2px gold';
      crownImageName = 'crown-1st.svg';
      break;

    case 2:
      rankShadow = '2px -2.2px #AAA';
      crownImageName = 'crown-2nd.svg';
      break;

    case 3:
      rankShadow = '2px -2.2px goldenrod';
      crownImageName = 'crown-3rd.svg';
      break;

    default:
      rankShadow = 'none';
      break;
  }

  return (
    <div
      className="leaderboardTableRow"
      style={style}
    >
      <p style={{ fontWeight: 900, margin: '1em', textShadow: rankShadow }}>
        {rank}
      </p>

      <div className="rowsContainer">
        {leaderboardRowGroup.positionLeaderboardRows.map(
          (positionLeaderboardRow, i) => (
            <div
              key={i}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {i > 0 && (
                <div
                  className="separator"
                  style={{ width: 'auto', margin: '0.5em', height: '2.5px' }}
                />
              )}

              <div className="positionLeaderboardRow">
                <ProfileCircle
                  crownImageName={crownImageName}
                  initials={positionLeaderboardRow.initials}
                  profileImageLocation={
                    positionLeaderboardRow.leaderboard_photo_location
                  }
                />

                <h5
                  style={{
                    margin: '1em',
                    fontSize: '0.9em',
                    textAlign: 'left',
                    flexGrow: 1,
                    wordBreak: 'break-all',
                  }}
                >
                  {positionLeaderboardRow.leaderboard_username}
                </h5>

                {positionLeaderboardRow.points && (
                  <p style={{ fontWeight: 900, margin: '1em' }}>
                    {positionLeaderboardRow.points.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )
        )}
      </div>

      <style jsx>{`
        .leaderboardTableRow {
          background-color: white;
          padding: 1em;
          width: 100%;
          border-radius: 4px;
          box-shadow: -1px 2px 5px lightgray;

          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 1em 0;
          font-size: 1.2em;
        }

        .rowsContainer {
          flex-grow: 1;
        }

        .positionLeaderboardRow {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
      `}</style>
    </div>
  );
}

interface ILeaderboardTableProps {
  leaderboardData: LeaderboardRowGroup[];
}

const LeaderboardTable: React.FC<ILeaderboardTableProps> = ({ leaderboardData }) => {
  const [expandedLeaderboard, setExpandedLeaderboard] = useState(false);

  return (
    <div className='table'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <p style={{ fontWeight: 'bold', margin: '1em 1em 0 1em' }}>
          Rank
        </p>

        <p style={{ fontWeight: 'bold', margin: '1em 1em 0 1em', textAlign: 'center', flexGrow: 1 }}>
          User
        </p>

        <p style={{ fontWeight: 'bold', margin: '1em 1em 0 1em' }}>
          Points
        </p>
      </div>

      { leaderboardData && leaderboardData.slice(0, expandedLeaderboard ? leaderboardData.length : 4).map((leaderboardRowGroup, i) =>
        <LeaderboardTableRow
          key={leaderboardRowGroup.position}
          rank={leaderboardRowGroup.position}
          leaderboardRowGroup={leaderboardRowGroup}
        />
      )}

      { leaderboardData && leaderboardData.length > 4 &&
        <div onClick={() => setExpandedLeaderboard(!expandedLeaderboard)} style={{ marginTop: '1.5em' }} className='moreLessButton'>
          SEE { expandedLeaderboard ? 'LESS' : 'MORE' }
          <Chevron
            direction='down'
            reversed={expandedLeaderboard}
            style={{
              color: colors.primaryBrandGray,
              fontSize: '1em',
              marginRight: '1em',
              height: 'auto'
            }}
          />
        </div>
      }

      <style jsx>{`
        .table {
          width: 100%;
          max-width: 600px;
          padding: 1em;
        }
        
        @media(min-width: 500px) {
          .table {
            padding: 1em 2em;
          }
        }
        
        .moreLessButton {
          display: flex;
          flex-direction: column;
          justify-content: flex-center;
          background-color: clear;
          font-weight: bold;
          letter-spacing: 0.3em;
          cursor: pointer;
          align-items: center;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default LeaderboardTable;